import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createEntityStore } from '@redpanda/utils';
import { toast } from 'react-hot-toast';
import { getEmployees, getArchivedEmployees, getAllEmployees, deleteEmployee as deleteEmployeeApi } from '../services/api';
import { storeMessages } from './storeMessages';

export interface Employee {
  id: string;
  employeeNumber?: string | null;
  name: string;
  idNumber?: string | null;
  email?: string | null;
  phone?: string | null;
  hireDate: string;
  resignationDate?: string | null;
  status: string;
  department?: string | null;
  position?: string | null;
  isArchived?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export const useEmployeeStoreBase = createEntityStore<Employee>({
  entityName: 'employee',
  fetchFn: (page: number) => getEmployees(page),
  deleteFn: deleteEmployeeApi,
  toast,
  messages: storeMessages,
});

export const useArchivedEmployeeStoreBase = createEntityStore<Employee>({
  entityName: 'archivedEmployee',
  fetchFn: (page: number) => getArchivedEmployees(page),
  deleteFn: deleteEmployeeApi,
  toast,
  messages: storeMessages,
});

/** 新增或編輯後寫回列表（封存列表裡編輯的員工也要更新；id 不在該列表時 updateItem 不動作） */
export const updateEmployeeInLists = (id: string, data: Partial<Employee>) => {
  useEmployeeStoreBase.getState().updateItem(id, data);
  useArchivedEmployeeStoreBase.getState().updateItem(id, data);
};

/**
 * 員工下拉選單（出勤、請假、薪資對話框、快速打卡共用）
 * stale-while-revalidate（R31，照 CRM fetchAllCustomers）：有快取先顯示，每次開對話框背景重抓，同時只送一個請求
 * 原本「快取永久有效」：別人新增的員工，下拉選單永遠看不到
 */
interface EmployeeOptionsState {
  employees: Employee[];
  loaded: boolean;
  fetchAll: () => Promise<void>;
}

let pendingFetch: Promise<void> | null = null;

export const useEmployeeOptionsStore = create<EmployeeOptionsState>()(
  persist(
    (set) => ({
      employees: [],
      loaded: false,
      fetchAll: () => {
        if (!pendingFetch) {
          pendingFetch = getAllEmployees()
            .then(response => set({ employees: response.data.data ?? [], loaded: true }))
            .catch(error => console.error('Failed to load employee options:', error))
            .finally(() => { pendingFetch = null; });
        }
        return pendingFetch;
      },
    }),
    { name: 'employee-options', version: 1, partialize: state => ({ employees: state.employees, loaded: state.loaded }) }
  )
);

/** 下拉選項：在職員工依姓名排序；編輯舊資料時目前選的員工（可能已離職）也要在清單裡 */
export const employeeOptions = (employees: Employee[], currentId?: string) =>
  employees
    .filter(e => e.status !== 'RESIGNED' || e.id === currentId)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(e => ({ value: e.id, label: e.department ? `${e.name}（${e.department}）` : e.name }));
